import React from 'react';

/**
 * Greyed "Coming soon" overlay/affordance.
 * Wraps children in a disabled container with a small chip.
 */
interface ComingSoonProps {
  children?: React.ReactNode;
  label?: string;
  inline?: boolean; // true = just a chip, no wrapper
}

export function ComingSoon({ children, label = 'Coming soon', inline = false }: ComingSoonProps) {
  if (inline) {
    return (
      <span
        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-chip text-[10px] font-medium text-muted"
        style={{ background: '#F2F4F7', border: '1px solid #E5EAF2' }}
      >
        {label}
      </span>
    );
  }

  return (
    <div className="relative">
      <div className="opacity-40 pointer-events-none select-none">{children}</div>
      <div
        className="absolute top-2 right-2 flex items-center gap-1 px-2 py-1 rounded-chip text-[10px] font-medium text-muted"
        style={{ background: '#F2F4F7', border: '1px solid #E5EAF2' }}
      >
        {label}
      </div>
    </div>
  );
}

/**
 * Full-page greyed placeholder (Calendar).
 */
export function GreyedPage({ title, icon, description }: { title: string; icon?: string; description?: string }) {
  return (
    <div className="relative">
      <div className="opacity-30 pointer-events-none select-none">
        {/* Fake skeleton */}
        <div className="flex gap-4 mb-4">
          {[1, 2, 3, 4].map((n) => (
            <div key={n} className="flex-1 h-24 bg-surface rounded-card shadow-card" />
          ))}
        </div>
        <div className="h-64 bg-surface rounded-card shadow-card" />
      </div>
      <div className="absolute inset-0 flex flex-col items-center justify-center gap-3">
        <div
          className="w-14 h-14 rounded-[14px] flex items-center justify-center"
          style={{ background: 'rgba(100,116,139,0.1)', border: '1px solid #E5EAF2' }}
        >
          <span className="text-2xl text-muted">{icon ?? '⏳'}</span>
        </div>
        <div className="text-center">
          <p
            className="text-[15px] font-semibold text-ink mb-1"
            style={{ fontFamily: '"Plus Jakarta Sans", system-ui' }}
          >
            {title}
          </p>
          {description && (
            <p className="text-[13px] text-muted max-w-xs">{description}</p>
          )}
        </div>
        <span
          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-chip text-[11px] font-semibold text-muted"
          style={{ background: '#F2F4F7', border: '1px solid #E5EAF2' }}
        >
          Coming soon
        </span>
      </div>
    </div>
  );
}
